import Modal from "./Modal";
import { ExplanationOfBenefit } from "src/shared/types";
import { capitalizeStatus } from "src/utils/utils";
import TableBadge from "../table/TableBadge";

type CoverageDetailsModalProps = {
  eob: ExplanationOfBenefit | null;
  onClose: () => void;
};

const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="py-2.5 flex justify-between items-baseline gap-4">
    <dt className="text-sm text-gray-500 shrink-0">{label}</dt>
    <dd className="text-sm font-medium text-gray-900 text-right min-w-0">{value ?? "—"}</dd>
  </div>
);

const CoverageDetailsModal = ({ eob, onClose }: CoverageDetailsModalProps) => {
  if (!eob) return null;

  const statusColorMap: Record<string, string> = {
    active: "bg-emerald-500",
    cancelled: "bg-rose-500",
    draft: "bg-amber-500",
    entered_in_error: "bg-sky-500",
  };

  return (
    <Modal title="Coverage details" onClose={onClose}>
      <div className="space-y-6">
        <section>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Summary</h3>
          <dl className="divide-y divide-gray-100">
            <DetailRow label="ID" value={eob.id} />
            <DetailRow
              label="Status"
              value={
                <TableBadge
                  text={capitalizeStatus(eob.status)}
                  bgColorClass={statusColorMap[eob.status] ?? "bg-gray-500"}
                />
              }
            />
            <DetailRow label="Use" value={eob.use} />
            <DetailRow label="Outcome" value={eob.outcome} />
            <DetailRow label="Disposition" value={eob.disposition} />
            <DetailRow label="Created" value={eob.created ? `${eob.created.date} ${eob.created.time?.slice(0, 8) ?? ""}`.trim() : null} />
            <DetailRow label="Billable period" value={eob.billablePeriod ? `${eob.billablePeriod.start} – ${eob.billablePeriod.end}` : null} />
          </dl>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Parties</h3>
          <dl className="divide-y divide-gray-100">
            <DetailRow label="Insurer" value={eob.insurer?.display} />
            <DetailRow label="Provider" value={eob.provider?.display} />
            <DetailRow label="Payee" value={eob.payee?.display} />
            <DetailRow label="Facility" value={eob.facility} />
          </dl>
        </section>

        {eob.insurance?.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Insurance</h3>
            <div className="space-y-4">
              {eob.insurance.map((cov, i) => (
                <dl key={cov.id || i} className="divide-y divide-gray-100">
                  <DetailRow label="Status" value={
                    <TableBadge
                      text={capitalizeStatus(cov.status)}
                      bgColorClass={statusColorMap[cov.status] ?? "bg-gray-500"}
                    />
                  } />
                  <DetailRow label="Subscriber ID" value={cov.subscriberId} />
                  <DetailRow label="Period" value={cov.period ? `${cov.period.start} – ${cov.period.end ?? "ongoing"}` : null} />
                  {cov.payors?.length > 0 && <DetailRow label="Payors" value={cov.payors.map((p) => p.display).join(", ")} />}
                  {cov.classes?.length > 0 && <DetailRow label="Classes" value={cov.classes.map((c) => `${c.name}: ${c.value}`).join("; ")} />}
                </dl>
              ))}
            </div>
          </section>
        )}

        {eob.payment?.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Payment</h3>
            <ul className="divide-y divide-gray-100">
              {eob.payment.map((p, i) => (
                <li key={i} className="py-2.5 flex justify-between items-baseline gap-4">
                  <span className="text-sm text-gray-500">{p.display}</span>
                  <span className="text-sm font-medium text-gray-900 tabular-nums">{p.amount?.value != null ? `${p.amount.value} ${p.amount.currency ?? ""}` : "—"}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {eob.items?.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Line items</h3>
            <div className="overflow-hidden rounded-lg border border-gray-100">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">Service</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Qty</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Date</th>
                    <th className="px-5 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {eob.items.map((item, i) => (
                    <tr key={i}>
                      <td className="px-5 py-3 text-gray-900">{item.productName ?? "—"}</td>
                      <td className="px-5 py-3 text-right text-gray-700">{item.quantity ?? "—"}</td>
                      <td className="px-5 py-3 text-right text-gray-700">{item.servicedDate ? `${item.servicedDate} ${item.servicedTime?.slice(0, 8) ?? ""}`.trim() : "—"}</td>
                      <td className="px-5 py-3 text-right tabular-nums text-gray-900">{item.value != null ? `${item.value} ${item.currency ?? ""}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {eob.netTotal?.length > 0 && (
          <section>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Net total</h3>
            <ul className="divide-y divide-gray-100">
              {eob.netTotal.map((t, i) => (
                <li key={i} className="py-2.5 flex justify-between items-baseline gap-4">
                  <span className="text-sm text-gray-500">{t.productName}</span>
                  <span className="text-sm font-medium text-gray-900 tabular-nums">{t.value != null ? `${t.value} ${t.currency ?? ""}` : "—"}</span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>
    </Modal>
  );
};

export default CoverageDetailsModal;
